import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import toast from 'react-hot-toast';

import { ColumnDef, SortingState } from '@tanstack/react-table';
import { capitalize } from '@eltoro-ui/creative_generator/Helpers';

import type { SidebarProps } from 'primereact/sidebar';
import type { MenuItem } from 'primereact/menuitem';
import type { MultiSelectChangeEvent, MultiSelectProps } from 'primereact/multiselect';
import { MultiSelect } from 'primereact/multiselect';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faCheck } from '@fortawesome/free-solid-svg-icons';

import Table from 'Components/Table';
import type { PanelRender, TablePaginationConfig, TableSortConfig } from 'Components/Table/types';

import { FlexBox } from '@eltoro-ui/components';
import { EmptyStatus } from 'Components';
import ConfirmModal from 'Pages/MyProfile/components/BillingSection/components/ConfirmModal';

import CreativeDetailsDrawer from 'Pages/CreativesLibrary/components/CreativeDetailsDrawer';
import CreativeActionAccessor from 'Pages/CreativesLibrary/components/CreativesList/elements/CreativeActionAccessor';
import RenameCreativeModal from 'Pages/CreativesLibrary/components/CreativesList/elements/RenameCreativeModal';
import CreativesFilterTag from 'Pages/CreativesLibrary/components/CreativesList/elements/CreativesFilterTag';

import type { CreativeFilter, CreativeStatusItem, CreativeTypeItem } from 'Pages/CreativesLibrary';

import CustomIcons from 'assets/icons';

import type { TCreative, TResPagination } from 'types';
import { CreativeStatusEnum, CreativeTypeEnum } from 'enums';

import { downloadCreative, formatBytes } from 'Utils/helpers';

import { deleteCreative } from 'Requests/Request_Methods/creativesLibraryMethods';

import './CreativesList.scss';

type CreativeActionTypes = 'delete' | 'view' | 'rename';

const creativesStatusItems: CreativeStatusItem[] = [
  { name: CreativeStatusEnum.UPLOADING },
  { name: CreativeStatusEnum.ACTIVE },
  { name: CreativeStatusEnum.ERROR },
];

const creativesTypeItems: CreativeTypeItem[] = [
  { name: CreativeTypeEnum.BANNER },
  { name: CreativeTypeEnum.VIDEO },
];

export type CreativesListProps = {
  creatives: TCreative[];
  loading: boolean;
  page: [number, Dispatch<SetStateAction<number>>];
  sort: [SortingState, Dispatch<SetStateAction<SortingState>>];
  creative_filter: [CreativeFilter, Dispatch<SetStateAction<CreativeFilter>>];
  pagination: [TResPagination, Dispatch<SetStateAction<TResPagination>>];
  updateCreativesListAfterDeletion: () => Promise<void>;
  updateCreativeAfterEditCreativeName: (
    creative: Pick<TCreative, 'name' | 'creative_uuid'>
  ) => void;
  searchValue: string;
};

const CreativesList = ({
  creatives,
  loading,
  sort: [sorting, setSorting],
  updateCreativesListAfterDeletion,
  updateCreativeAfterEditCreativeName,
  page: [page, setPage],
  pagination: [pagination],
  creative_filter: [creative_filter, setCreativeFilter],
  searchValue,
}: CreativesListProps) => {
  const [creativeAction, setCreativeAction] = useState<CreativeActionTypes | null>(null);
  const [selectedCreative, setSelectedCreative] = useState<TCreative | null>(null);
  const [deleteCreativeLoading, setDeleteCreativeLoading] = useState<boolean>(false);

  const [creativeDetailsView, setCreativeDetailsView] = useState<boolean>(false);

  const [creativeStatuses, setCreativeStatuses] = useState<CreativeStatusItem[]>([]);
  const [creativeTypes, setCreativeTypes] = useState<CreativeTypeItem[]>([]);

  useEffect(() => {
    setSorting([]);
    setCreativeStatuses(creative_filter.status_type);
    setCreativeTypes(creative_filter.creative_type);
  }, [creative_filter]);

  const onHideCreativeDetailsView: SidebarProps['onHide'] = () => {
    setSelectedCreative(null);
    setCreativeAction(null);
    setCreativeDetailsView(false);
  };

  const onRenameCreativeAction =
    (creative: TCreative): MenuItem['command'] =>
    () => {
      setSelectedCreative(creative);
      setCreativeAction('rename');
    };

  const onDeleteCreativeAction =
    (creative: TCreative): MenuItem['command'] =>
    () => {
      setSelectedCreative(creative);
      setCreativeAction('delete');
    };

  const onViewDetailsAction =
    (creative: TCreative): MenuItem['command'] =>
    () => {
      setSelectedCreative(creative);
      setCreativeAction('view');
      setCreativeDetailsView(true);
    };

  const onDownloadCreativeAction =
    (creative: TCreative): MenuItem['command'] =>
    () =>
      downloadCreative(creative);

  const columns: ColumnDef<TCreative>[] = [
    {
      header: '',
      id: 'preview',
      size: 72,
      enableResizing: false,
      enableSorting: false,
      cell: ({
        row: {
          original: { presigned_url, thumb_presigned_url, creative_type, name, file_type, status },
        },
      }) =>
        presigned_url && status !== CreativeStatusEnum.UPLOADING ? (
          <div className="creative-preview">
            {creative_type === CreativeTypeEnum.BANNER ||
            (creative_type === CreativeTypeEnum.VIDEO && thumb_presigned_url) ? (
              <img src={thumb_presigned_url || presigned_url} alt={name} />
            ) : (
              <video>
                <source src={presigned_url} type={`video/${file_type}`} />
                <track src="captions_en.vtt" kind="captions" label="english_captions" />
              </video>
            )}
            {creative_type === CreativeTypeEnum.VIDEO && (
              <CustomIcons
                className="creative-video-icon"
                name="video_play"
                color="#FFAB03"
                fontSize={12}
              />
            )}
          </div>
        ) : (
          <CustomIcons name="beewo_logo_creatives" fontSize={36} />
        ),
    },
    {
      header: 'Creative Name',
      accessorKey: 'name',
    },
    {
      header: 'File Type',
      accessorKey: 'file_type',
      accessorFn: creative => `.${creative.file_type}`,
    },
    {
      header: 'Creative Type',
      accessorKey: 'creative_type',
      accessorFn: creative => capitalize(creative.creative_type),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({
        row: {
          original: { status },
        },
      }) => (
        <div className={classNames('creative-status', status)}>
          {status === CreativeStatusEnum.UPLOADING ? `${status}...` : status}
        </div>
      ),
    },
    {
      header: 'File Size',
      accessorKey: 'file_size',
      accessorFn: ({ file_size }) => formatBytes(file_size),
    },
    {
      header: 'Specs',
      accessorKey: 'file_specs',
    },
    {
      header: 'Order Lines',
      accessorKey: 'orderline_count',
      cell: ({
        row: {
          original: { orderline_count },
        },
      }) => orderline_count || '-',
      sortDescFirst: false,
    },
    {
      header: 'Creative ID',
      accessorKey: 'eltoro_creative_id',
      accessorFn: ({ eltoro_creative_id }) => eltoro_creative_id ?? '-',
    },
    {
      header: 'Date Created',
      accessorKey: 'created_at',
      accessorFn: ({ created_at }) => moment(created_at).format('MM/DD/YYYY'),
    },
    {
      header: '',
      id: 'actions',
      size: 77,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row: { original: creative } }) => (
        <CreativeActionAccessor
          creative={creative}
          onRenameCreativeAction={onRenameCreativeAction}
          onDeleteCreativeAction={onDeleteCreativeAction}
          onViewDetailsAction={onViewDetailsAction}
          onDownloadCreativeAction={onDownloadCreativeAction}
        />
      ),
    },
  ];

  const onChangeCreativeStatuses = ({ value }: MultiSelectChangeEvent) =>
    setCreativeStatuses(value);

  const onChangeCreativeTypes = ({ value }: MultiSelectChangeEvent) => setCreativeTypes(value);

  const onCreativeStatusDelete = (name: CreativeStatusEnum | CreativeTypeEnum) => () =>
    setCreativeFilter(creativeFilters => ({
      ...creativeFilters,
      status_type: creativeFilters.status_type.filter(status => status.name !== name),
    }));

  const onCreativeTypeDelete = (name: CreativeStatusEnum | CreativeTypeEnum) => () =>
    setCreativeFilter(creativeFilters => ({
      ...creativeFilters,
      creative_type: creativeFilters.creative_type.filter(status => status.name !== name),
    }));

  const handleApplyFilters = (name: string) => {
    if (name === 'type')
      setCreativeFilter(creativeFilters => ({
        ...creativeFilters,
        creative_type: creativeTypes,
      }));

    if (name === 'status')
      setCreativeFilter(creativeFilters => ({
        ...creativeFilters,
        status_type: creativeStatuses,
      }));

    setPage(1);
  };

  const onHide =
    (name: string): MultiSelectProps['onHide'] =>
    () => {
      if (
        (name === 'type' &&
          (creativeTypes.length !== creative_filter.creative_type.length ||
            creativeTypes.some(
              ({ name }) => !creative_filter.creative_type.find(type => type.name === name)
            ))) ||
        (name === 'status' &&
          (creativeStatuses.length !== creative_filter.status_type.length ||
            creativeStatuses.some(
              ({ name }) => !creative_filter.status_type.find(status => status.name === name)
            )))
      )
        handleApplyFilters(name);
    };

  const PanelFooterTemplate: MultiSelectProps['panelFooterTemplate'] = (_, hide) => {
    return (
      <FlexBox
        UNSAFE_className="creative-filter-panel-footer"
        alignItems="center"
        justifyContent="flex-end"
      >
        <button className="creative-filter-panel-footer-apply-btn" onClick={hide}>
          Apply
        </button>
      </FlexBox>
    );
  };

  const onClear = () => {
    setCreativeFilter({ creative_type: [], status_type: [] });
    setPage(1);
  };

  const header: PanelRender<TCreative> = () => {
    return (
      <FlexBox flexDirection="column" gap="20px">
        <FlexBox alignItems="center" gap="20px">
          <MultiSelect
            name="type"
            className="creative-filter-select"
            itemClassName="creative-filter-item"
            panelClassName="creative-filter-panel"
            options={creativesTypeItems}
            optionLabel="name"
            value={creativeTypes}
            fixedPlaceholder
            placeholder="Type"
            onChange={onChangeCreativeTypes}
            appendTo="self"
            checkboxIcon={
              <FontAwesomeIcon icon={faCheck} color="#FFAB03" style={{ fontSize: 12 }} />
            }
            dropdownIcon={
              <FontAwesomeIcon icon={faAngleDown} color="#757575" style={{ fontSize: 18 }} />
            }
            panelHeaderTemplate={() => null}
            panelFooterTemplate={PanelFooterTemplate}
            onHide={onHide('type')}
            style={{ width: 140 }}
          />
          <MultiSelect
            name="status"
            className="creative-filter-select"
            itemClassName="creative-filter-item"
            panelClassName="creative-filter-panel"
            options={creativesStatusItems}
            optionLabel="name"
            value={creativeStatuses}
            fixedPlaceholder
            placeholder="Status"
            onChange={onChangeCreativeStatuses}
            appendTo="self"
            panelHeaderTemplate={() => null}
            checkboxIcon={
              <FontAwesomeIcon icon={faCheck} color="#FFAB03" style={{ fontSize: 12 }} />
            }
            dropdownIcon={
              <FontAwesomeIcon icon={faAngleDown} color="#757575" style={{ fontSize: 18 }} />
            }
            panelFooterTemplate={PanelFooterTemplate}
            onHide={onHide('status')}
            style={{ width: 140 }}
          />
        </FlexBox>
        {(!!creative_filter.status_type.length || !!creative_filter.creative_type.length) && (
          <FlexBox alignItems="center" gap="12px">
            {creative_filter.creative_type.map(({ name }) => (
              <CreativesFilterTag key={name} name={name} onDelete={onCreativeTypeDelete} />
            ))}
            {creative_filter.status_type.map(({ name }) => (
              <CreativesFilterTag key={name} name={name} onDelete={onCreativeStatusDelete} />
            ))}
            <button className="clear-filters" onClick={onClear}>
              Clear
            </button>
          </FlexBox>
        )}
      </FlexBox>
    );
  };

  const onCancelDelete = () => {
    setSelectedCreative(null);
    setCreativeAction(null);
  };

  const onDeleteCreative = useCallback(async () => {
    if (selectedCreative) {
      try {
        setDeleteCreativeLoading(true);
        await deleteCreative(selectedCreative.creative_uuid);

        toast.success('Creative deleted successfully!');
        updateCreativesListAfterDeletion();
      } catch (e) {
        if ('detail' in e && typeof e.detail === 'string') toast.error(e.detail);
        else toast.error('Something went wrong!');
      } finally {
        setDeleteCreativeLoading(false);
        onCancelDelete();
      }
    }
  }, [selectedCreative]);

  const tablePagination = useMemo<TablePaginationConfig>(
    () => ({
      hideOnSinglePage: true,
      pageSize: 10,
      current: page,
      total: pagination.total_count,
      totalPages: pagination.total_pages,
      onChange: page => setPage(page),
    }),
    [page, pagination]
  );

  const sortConfig: TableSortConfig = {
    sorting,
    onSortingChange: setSorting,
  };

  return (
    <>
      <Table<TCreative>
        className="creatives-table"
        columns={columns}
        data={creatives}
        rowDisabled={creative => creative.status === CreativeStatusEnum.UPLOADING}
        loading={loading}
        title={header}
        sortConfig={sortConfig}
        pagination={tablePagination}
        emptyText={
          <EmptyStatus
            heading={
              searchValue && !creatives.length
                ? 'No results found'
                : 'Your creatives library is empty.'
            }
            subHeading={
              searchValue && !creatives.length
                ? 'You might consider trying different keywords, double-checking for any spelling errors, or adjusting your filters.'
                : 'Start uploading assets in your library so you can use them for your campaigns later.'
            }
          />
        }
      />
      {selectedCreative && creativeAction === 'delete' && (
        <ConfirmModal
          type="delete"
          title="Delete Creative?"
          content={
            <span>
              Are you sure you want to delete{' '}
              <b>
                {selectedCreative.name}.{selectedCreative.file_type}
              </b>
              ?
            </span>
          }
          loading={deleteCreativeLoading}
          onOk={onDeleteCreative}
          okText="Yes, Delete"
          onCancel={onCancelDelete}
        />
      )}
      {selectedCreative && creativeAction === 'rename' && (
        <RenameCreativeModal
          creative={selectedCreative}
          onCancel={onCancelDelete}
          updateCreativeAfterEditCreativeName={updateCreativeAfterEditCreativeName}
        />
      )}
      <CreativeDetailsDrawer
        creative_uuid={selectedCreative?.creative_uuid}
        visible={creativeDetailsView}
        onHide={onHideCreativeDetailsView}
        updateCreativeAfterEditCreativeName={updateCreativeAfterEditCreativeName}
      />
    </>
  );
};

export default CreativesList;
